//
// # Parasitics Panel Types
//
// Type definitions for parasitic extraction results displayed in the panel.
// These match the Rust ExtractParasiticsResult structure from RustClient.
//

import { validate as v } from "CoreUtils";
import typia from "typia";

/** Per-net breakdown of parasitic values (matches Rust NetParasiticDetail) */
export interface NetSummary {
  name: string;
  wireResistance: number;
  viaResistance: number;
  groundCapacitance: number;
  couplingCapacitance: number;
  segmentCount: number;
  viaCount: number;
}

/** Coupling capacitance between two nets (matches Rust CouplingPair) */
export interface CouplingPair {
  netA: string;
  netB: string;
  capacitance: number;
}

/** Extraction statistics (matches Rust ParasiticsStats) */
export interface ParasiticsStats {
  routesProcessed: number;
  segmentsExtracted: number;
  viasExtracted: number;
  couplingPairsFound: number;
  extractionTimeUs: number;
}

/** Complete parasitic extraction result (matches Rust ExtractParasiticsResult) */
export interface ParasiticsResult {
  /** The stringified ident of the layout */
  identKey: string;
  /** Total wire resistance across all nets in Ohms */
  totalWireResistance: number;
  /** Total via resistance across all nets in Ohms */
  totalViaResistance: number;
  /** Total ground capacitance across all nets in fF */
  totalGroundCapacitance: number;
  /** Total coupling capacitance in fF */
  totalCouplingCapacitance: number;
  /** Per-net breakdown, sorted by total capacitance descending */
  netSummaries: NetSummary[];
  /** Top coupling pairs between different nets, sorted by capacitance descending */
  topCouplingPairs: CouplingPair[];
  /** Extraction statistics */
  stats: ParasiticsStats;
}

//
// Legacy types for backward compatibility during migration
//

/** @deprecated Use ParasiticsResult instead */
export interface ParasiticsSummary {
  totalSegments: number;
  totalVias: number;
  totalCouplingPairs: number;
  totalWireResistance: number;
  totalViaResistance: number;
  totalGroundCapacitance: number;
  totalCouplingCapacitance: number;
  netSummaries: NetSummary[];
  topCouplingPairs: CouplingPair[];
}

/** @deprecated Use ParasiticsResult instead */
export interface ParasiticsStatus {
  identKey?: string;
  enabled: boolean;
  segmentCount?: number;
  viaCount?: number;
  summary?: ParasiticsSummary;
}

//
// API Response Validators
//

/** Response from run-pex endpoint (matches Rust ExtractParasiticsResult with camelCase) */
export interface PexResultResponse {
  totalWireResistanceOhms: number;
  totalViaResistanceOhms: number;
  totalGroundCapacitanceFf: number;
  totalCouplingCapacitanceFf: number;
  netDetails: {
    netName: string;
    wireResistanceOhms: number;
    viaResistanceOhms: number;
    groundCapacitanceFf: number;
    couplingCapacitanceFf: number;
    segmentCount: number;
    viaCount: number;
  }[];
  topCouplingPairs: {
    netA: string;
    netB: string;
    capacitanceFf: number;
  }[];
  stats: {
    routesProcessed: number;
    segmentsExtracted: number;
    viasExtracted: number;
    couplingPairsFound: number;
    extractionTimeUs: number;
  };
}
export const PexResultResponse = {
  validator: new v.Validator(typia.createValidate<PexResultResponse>()),
};

/** @deprecated Use PexResultResponse */
export interface ParasiticsStatusResponse {
  enabled: boolean;
  segmentCount?: number;
  viaCount?: number;
}
export const ParasiticsStatusResponse = {
  validator: new v.Validator(typia.createValidate<ParasiticsStatusResponse>()),
};

/** @deprecated Use PexResultResponse */
export interface ParasiticsSummaryResponse {
  totalSegments: number;
  totalVias: number;
  totalCouplingPairs: number;
  totalWireResistance: number;
  totalViaResistance: number;
  totalGroundCapacitance: number;
  totalCouplingCapacitance: number;
  netSummaries: NetSummary[];
  topCouplingPairs: CouplingPair[];
}
export const ParasiticsSummaryResponse = {
  validator: new v.Validator(typia.createValidate<ParasiticsSummaryResponse>()),
};
